import { Fragment, useState } from "react";
import { Globe } from "lucide-react";
import { api } from "../lib/api";

const ordered = (text, sources) => {
  const seen = [...new Set([...text.matchAll(/\[web:(\d+)\]/g)].map((m) => Number(m[1])))];
  const byId = Object.fromEntries(sources.map((s) => [s.id, s]));
  const first = seen.map((id) => byId[id]).filter(Boolean);
  return [...first, ...sources.filter((s) => !seen.includes(s.id))];
};

function Cited({ text, sources }) {
  const order = sources.map((s) => s.id);
  return text.split(/(\[web:\d+\])/g).map((part, i) => {
    const m = part.match(/\[web:(\d+)\]/);
    if (!m) return <Fragment key={i}>{part.replace(/\*\*/g, "")}</Fragment>;
    const idx = order.indexOf(Number(m[1]));
    if (idx < 0) return null;
    return <a key={i} href={sources[idx].url} target="_blank" rel="noopener noreferrer" className="align-super text-[10px] f-mono text-[var(--signal)] ml-0.5">{idx + 1}</a>;
  });
}

export default function LiveContext({ slug, path, title = "Where this piece meets the world right now." }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try { const r = await api.get(path || `/context/${slug}`, { timeout: 120000 }); setData(r.data); }
    catch (e) { setErr(e?.response?.data?.detail || "The wire went quiet. Try again shortly."); }
    setBusy(false);
  };

  const sources = data ? ordered(data.text || "", Array.isArray(data.sources) ? data.sources : []) : [];

  return (
    <section className="border border-[var(--line)] p-5 sm:p-8" data-testid="live-context-panel">
      <p className="eyebrow mb-3">Live context &middot; from the web</p>
      {!data ? (
        <>
          <p className="f-serif italic text-xl sm:text-2xl leading-snug mb-6">{title}</p>
          <button onClick={load} disabled={busy} className="flex items-center gap-3 f-mono text-xs uppercase tracking-[0.2em] border border-[var(--bone)] px-5 py-3 hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors disabled:opacity-60" data-testid="live-context-load-button">
            <Globe size={14} className={busy ? "animate-spin" : ""} /> {busy ? "Searching the wire\u2026" : "Find the signal"}
          </button>
          {err && <p className="text-sm text-[var(--signal)] mt-3" data-testid="live-context-error">{err}</p>}
        </>
      ) : (
        <div data-testid="live-context-result">
          <p className="text-lg leading-relaxed text-[#d8d7d1]"><Cited text={data.text || ""} sources={sources} /></p>
          <ol className="mt-6 space-y-2 border-t border-[var(--line)] pt-4">
            {sources.map((s, i) => (
              <li key={s.url} className="flex gap-3 text-sm">
                <span className="f-mono text-[11px] text-[var(--signal)] pt-0.5">{i + 1}</span>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="link-u text-[#cfcec8] break-words" data-testid={`live-context-source-${i + 1}`}>{s.title || s.url}</a>
              </li>
            ))}
          </ol>
          <p className="f-mono text-[11px] text-[var(--ash)] mt-5">AI summary of web sources via Perplexity &middot; refreshed daily</p>
        </div>
      )}
    </section>
  );
}
